/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903853583 — Знижки */

import { getCurrencyBySubUnits, isPositiveNumber } from '../../../shared';
import { debug } from '../../../shared/lib/logging';

interface UseDiscountedPriceParams {
    /** Authoritative post-discount amount in subunits (already produced by FE submitAmount or PHP for CT1). */
    amountInSubunits: number;
    /** Pre-discount amount in currency units (user-typed CT5 input, useVolumeOptions originalAmount, etc.). */
    inputAmount?: number;
    /** Only used to gate `hasDiscount`; this hook NEVER applies the discount itself. */
    discountPercentage?: number | null;
}

interface DiscountedPriceViewModel {
    /** Pre-discount price for the strike-through (currency units). Equals discountedAmount when no inputAmount. */
    originalAmount: number;
    /** Post-discount price for the big number (currency units). Comes straight from amountInSubunits. */
    discountedAmount: number;
    /** originalAmount − discountedAmount; 0 when no discount applies. */
    savings: number;
}

export const useDiscountedPrice = ({
    amountInSubunits,
    inputAmount,
    discountPercentage,
}: UseDiscountedPriceParams): DiscountedPriceViewModel => {
    const safeSubunits = isPositiveNumber(amountInSubunits) ? amountInSubunits : 0;
    const discountedAmount = getCurrencyBySubUnits(safeSubunits)
    const originalAmount = isPositiveNumber(inputAmount) ? inputAmount : discountedAmount;

    const isAmountWithDiscount = originalAmount > discountedAmount && isPositiveNumber(discountedAmount);
    const isDiscountAvailable = isPositiveNumber(discountPercentage) && isAmountWithDiscount;

    const savings = isDiscountAvailable ? originalAmount - discountedAmount : 0;

    debug('Price display:', 'info', {
        amountInSubunits,
        inputAmount,
        discountPercentage,
        originalAmount,
        discountedAmount,
        isDiscountAvailable,
        savings,
    });

    return {
        originalAmount,
        discountedAmount,
        savings,
    };
};
